import { PropsWithChildren, useEffect, useState } from 'react';

import { useAuth, EXTERNAL_USER_ROLE_IDS } from '@features/auth/hooks/useAuth';
import { USER_ROLES } from '@features/auth/utils/constants.ts';
import { findCurrentUserRoleData, findInternalUserCountryCode } from '@features/auth/utils/findCurrentUserRoleData.ts';
import { useRolesDetailsContext } from '@shared/providers/RolesDetailsProvider/useRolesDetailsContext.tsx';
import { UserDetails, UserDetailsContext } from '@shared/providers/UserDetailsProvider/providerSetup.ts';
import { UserCountryCodes } from '@shared/types/countryCodes.ts';

export function UserDetailsProvider({ children }: PropsWithChildren) {
  const [userDetailsState, setUserDetailsState] = useState<UserDetails | undefined>(undefined);
  const { getRoleId, getUserId, getUserCountryCode } = useAuth();
  const rolesDetailsContext = useRolesDetailsContext();

  useEffect(() => {
    (async () => {
      const userRoleId = await getRoleId();
      const userId = await getUserId();

      if (!userId || !userRoleId) {
        return;
      }

      const isExternalDirectClient = userRoleId === EXTERNAL_USER_ROLE_IDS.DIRECT_CLIENT_ID;
      const isExternalAgency = userRoleId === EXTERNAL_USER_ROLE_IDS.AGENCY_ID;

      const isAnyExternalUser = isExternalDirectClient || isExternalAgency;

      const currentUserRoleData = findCurrentUserRoleData({
        rolesDetails: rolesDetailsContext,
        userRoleId,
        isExternalDirectClient,
        isExternalAgency,
      });

      if (!currentUserRoleData) {
        return;
      }

      const userCountryCode = isAnyExternalUser
        ? await getUserCountryCode()
        : findInternalUserCountryCode(currentUserRoleData, userRoleId);

      const isPortfolioSalesUser = currentUserRoleData.name === USER_ROLES.SALES_PORTFOLIO_USER;
      const isSuperAdminUser = currentUserRoleData.name === USER_ROLES.SUPER_ADMIN;
      const isSalesAdminUser = currentUserRoleData.name === USER_ROLES.SALES_ADMIN;

      const isItalianUser = userCountryCode === UserCountryCodes.IT;
      const isSpanishUser = userCountryCode === UserCountryCodes.ES;
      const isGermanUser = userCountryCode === UserCountryCodes.DE;

      setUserDetailsState((prevState) => ({
        ...prevState,
        role: currentUserRoleData.name,
        id: userId,
        countryCode: userCountryCode,
        isPortfolioSalesUser,
        isExternalAgency,
        isExternalDirectClient,
        isAnyExternalUser,
        isSuperAdminUser,
        isSalesAdminUser,
        isItalianUser,
        isSpanishUser,
        isGermanUser,
      }));
    })();
  }, [rolesDetailsContext]);

  return <UserDetailsContext.Provider value={userDetailsState}>{children}</UserDetailsContext.Provider>;
}
